import {
  deleteLibraryItem,
  listLibraryItems,
  uploadLibraryFile,
} from './sharepoint';

const LIST = 'teamMemberResources';

/**
 * Team Member Resources live in a SharePoint *document library* — the
 * same approach used by Sales Resources. Directors upload files (onboarding
 * decks, SOPs, training videos, quick-reference PDFs) and every signed-in
 * team member can view the tiles and download. Storage inherits the
 * tenant's permissions / versioning / retention, so we never re-roll our
 * own upload pipeline.
 *
 * Parallel of services/salesResources.js — the two libraries are separate
 * so Directors can tune categories, permissions, or retention differently
 * between "internal training" and "customer-facing sales collateral".
 */

/**
 * List every resource in the library, newest first (by PostedAt, which
 * we set on upload). Each row has:
 *   - id, driveItemId, fileName, sizeBytes, downloadUrl, webUrl
 *   - Title, Description, Category, PostedBy, PostedAt
 */
export async function listTeamMemberResources(opts) {
  const rows = await listLibraryItems(LIST, opts);
  // Newest-upload first. Fall back to Modified / Created for rows that
  // predate the PostedAt stamp so the sort still produces a sensible
  // order rather than dropping legacy uploads to the bottom.
  return rows.sort((a, b) => {
    const ta = Date.parse(a.PostedAt || a.Modified || a.Created || '') || 0;
    const tb = Date.parse(b.PostedAt || b.Modified || b.Created || '') || 0;
    return tb - ta;
  });
}

/**
 * Upload a resource. `file` is a browser File; the remaining fields are
 * stored as list-item columns alongside the uploaded file. Title defaults
 * to the file's base name so uploaders can skip the field when they're
 * happy with what SharePoint derives from the filename.
 */
export async function createTeamMemberResource({
  file,
  Title,
  Description = '',
  Category = '',
  PostedBy = '',
  PostedAt,
}) {
  if (!file) throw new Error('createTeamMemberResource: file is required.');
  const fallbackTitle = (file.name || '').replace(/\.[^.]+$/, '');
  const fields = {
    Title: (Title || fallbackTitle || 'Untitled').trim(),
    Description,
    Category,
    PostedBy,
    PostedAt: PostedAt || new Date().toISOString(),
  };
  return uploadLibraryFile(LIST, file, fields);
}

export const deleteTeamMemberResource = (id) => deleteLibraryItem(LIST, id);
